import React, { Fragment } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { decreaseCartItemQty, increaseCartItemQty, removeItemFriomCart } from '../../slices/cartSlice';
import CheckoutSteps from './CheckoutSteps';

export const Cart = () => {
    const navigate = useNavigate();
    const { items } = useSelector((state) => state.cartState);
    const { isAuthenticated } = useSelector((state) => state.authState);
    const dispatch = useDispatch();

    const increaseQty = (item) => {
        if (item.stock === 0 || item.quantity >= item.stock) return;
        dispatch(increaseCartItemQty(item.product));
    };

    const decreaseQty = (item) => {
        if (item.quantity === 1) return;
        dispatch(decreaseCartItemQty(item.product));
    };

    const handleCheckout = () => {
        if (isAuthenticated) {
            navigate('/shipping');
        } else {
            navigate('/login?redirect=shipping');
        }
    };

    const calculateItemPrice = (item) => {
        return (item.price / 8 * item.weight) + (item.price * item.makingrate / 100);
    };

    const totalPrice = items.reduce((acc, item) => (
        acc + item.quantity * calculateItemPrice(item)
    ), 0);

    return (
        <Fragment>
            <div className="bg-white min-h-screen pt-24 pb-32">
                <div className="container mx-auto px-4">
                    {/* Header & Steps */}
                    <div className="max-w-4xl mx-auto mb-16">
                        <h1 className="text-4xl lg:text-5xl font-serif font-bold text-center text-gray-900 mb-8 whitespace-nowrap overflow-hidden text-ellipsis">Your Shopping Bag</h1>
                        <CheckoutSteps />
                    </div>

                    {items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                            <div className="w-24 h-24 mb-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Your bag is currently empty.</h2>
                            <p className="text-gray-400 mb-10 max-w-sm text-center">Discover our handcrafted collections and find something extraordinary for your next occasion.</p>
                            <Link to="/" className="bg-gray-900 text-white px-10 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-yellow-600 transition-all shadow-xl hover:shadow-yellow-600/20">
                                Explore Collections
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-start">
                            {/* Line Items */}
                            <div className="lg:col-span-2 space-y-8">
                                <div className="flex items-center justify-between pb-6 border-b border-gray-100">
                                    <h3 className="text-xs font-bold tracking-widest text-gray-400 uppercase">{items.length} Items Selected</h3>
                                    <Link to="/" className="text-xs font-bold tracking-widest text-yellow-600 hover:text-gray-900 transition-colors uppercase">Continue Shopping</Link>
                                </div>
                                
                                {items.map((item) => (
                                    <div key={item.product} className="flex flex-col sm:flex-row items-center gap-8 py-8 border-b border-gray-50 group hover:bg-gray-50/50 transition-all rounded-2xl px-4 translate-x-[-1rem] sm:translate-x-0">
                                        <div className="w-32 h-32 bg-gray-50 rounded-2xl overflow-hidden shrink-0 border border-gray-100 shadow-sm relative">
                                            <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        </div>
                                        
                                        <div className="flex-1 text-center sm:text-left">
                                            <div className="text-[10px] text-yellow-600 font-bold tracking-widest uppercase mb-1">{item.category || "Jewellery"}</div>
                                            <Link to={`/product/${item.product}`} className="text-xl font-serif font-bold text-gray-900 hover:text-yellow-600 transition-colors mb-2 block">{item.name}</Link>
                                            <div className="flex items-center justify-center sm:justify-start space-x-3 text-xs text-gray-400 mb-6">
                                                <span>{item.weight} Grams</span>
                                                <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                                                <span>22K Gold purity</span>
                                            </div>

                                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6">
                                                <div className="flex items-center bg-white rounded-xl p-0.5 border border-gray-200 shadow-sm">
                                                    <button onClick={() => decreaseQty(item)} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 font-bold text-lg">−</button>
                                                    <span className="w-10 text-center font-bold text-gray-900">{item.quantity}</span>
                                                    <button onClick={() => increaseQty(item)} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 font-bold text-lg">+</button>
                                                </div>
                                                
                                                <button 
                                                    onClick={() => dispatch(removeItemFriomCart(item.product))}
                                                    className="text-[10px] font-bold text-red-400 hover:text-red-600 tracking-widest uppercase flex items-center gap-2 border-b border-transparent hover:border-red-600 pb-0.5 transition-all"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Remove from bag
                                                </button>
                                            </div>
                                        </div>

                                        <div className="sm:text-right">
                                            <div className="text-xl font-bold text-gray-900">
                                                Rs {calculateItemPrice(item).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </div>
                                            <div className="text-[9px] text-gray-400 uppercase mt-1 tracking-widest">Per Unit Price</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Order Summary */}
                            <div className="lg:sticky lg:top-32 h-fit bg-gray-900 rounded-3xl p-10 text-white shadow-2xl shadow-gray-200">
                                <h2 className="text-2xl font-serif font-bold mb-8">Order Summary</h2>
                                
                                <div className="space-y-6 pb-8 border-b border-white/10 mb-8">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/60 font-light tracking-wide uppercase text-[10px]">Total Items</span>
                                        <span className="font-bold">{items.reduce((acc, item) => (acc + item.quantity), 0)} Units</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/60 font-light tracking-wide uppercase text-[10px]">Standard Delivery</span>
                                        <span className="font-bold text-yellow-500 uppercase text-[10px]">Complimentary</span>
                                    </div>
                                </div>

                                <div className="flex justify-between items-end mb-12">
                                    <span className="text-white/60 font-light tracking-wide uppercase text-[10px] pb-1">Grand Estimated Total</span>
                                    <span className="text-3xl font-bold text-yellow-500">
                                        Rs {totalPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </span>
                                </div>

                                <button 
                                    onClick={handleCheckout}
                                    className="w-full bg-white text-gray-900 py-5 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-yellow-600 hover:text-white transition-all shadow-xl active:scale-95"
                                >
                                    Proceed to Checkout
                                </button>
                                
                                <div className="mt-8 flex items-center justify-center space-x-4 opacity-40">
                                    <img src="/images/visa.png" alt="Visa" className="h-4 grayscale invert" />
                                    <img src="/images/mastercard.png" alt="Master" className="h-4 grayscale invert" />
                                    <img src="/images/secure.png" alt="Secure" className="h-4 grayscale invert" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};
